/**
 * TypeScript types for Document Cross-Check Validation Rules
 * Corresponds to backend Pydantic schemas in api/routers/document_validation.py
 */

/**
 * Comparison Type enum
 */
export enum ComparisonType {
    IDENTITY = "identity",
    COHERENCE = "coherence",
    TEMPORAL = "temporal",
    HIERARCHICAL = "hierarchical",
}

/**
 * Validation Severity enum
 */
export enum ValidationSeverity {
    INFO = "info",
    WARNING = "warning",
    ERROR = "error",
    CRITICAL = "critical",
}

/**
 * Tolerance Configuration for numeric/date comparisons
 */
export interface ToleranceConfig {
    type: "percentage" | "absolute" | "days";
    value: number;
    unit?: string;
    operator?: "<=" | ">=" | "<" | ">" | "=";
}

/**
 * Validation Rule Response
 */
export interface ValidationRuleResponse {
    cross_check_rule_id: string;
    rule_name: string;
    rule_code: string;
    description: string | null;

    source_document_type: string;
    target_document_type: string;
    source_attribute_id: string;
    target_attribute_id: string;

    comparison_type: string;
    precedence: string | null;
    is_blocking: boolean;
    severity_if_fail: string;

    tolerance_config: ToleranceConfig | null;
    validation_function: string | null;
    validation_parameters: Record<string, unknown> | null;

    is_active: boolean;
    execution_order: number;
    execution_count: number;
    failure_count: number;
    last_executed_at: string | null;

    created_at: string;
    updated_at: string;
}

/**
 * Validation Rule Create Request
 */
export interface ValidationRuleCreate {
    rule_name: string;
    rule_code: string;
    description?: string | null;

    source_document_type: string;
    target_document_type: string;
    source_attribute_id: string;
    target_attribute_id: string;

    comparison_type: ComparisonType;
    precedence?: string | null;
    is_blocking?: boolean;
    severity_if_fail?: ValidationSeverity;

    tolerance_config?: ToleranceConfig | null;
    validation_function?: string | null;
    validation_parameters?: Record<string, unknown> | null;

    is_active?: boolean;
    execution_order?: number;
}

/**
 * Validation Rule Update Request
 */
export interface ValidationRuleUpdate {
    rule_name?: string | null;
    description?: string | null;
    source_document_type?: string | null;
    target_document_type?: string | null;
    source_attribute_id?: string | null;
    target_attribute_id?: string | null;
    comparison_type?: ComparisonType | null;
    is_blocking?: boolean | null;
    severity_if_fail?: ValidationSeverity | null;
    tolerance_config?: ToleranceConfig | null;
    validation_parameters?: Record<string, unknown> | null;
    is_active?: boolean | null;
    execution_order?: number | null;
}

/**
 * Validation Rule List Filters
 */
export interface ValidationRuleListFilters {
    skip?: number;
    limit?: number;
    is_active?: boolean;
    comparison_type?: ComparisonType;
    source_document_type?: string;
    target_document_type?: string;
}
